import { DropdownMenu, Icon } from '@paypalcorp/pp-react'
import React, {
  EventHandler,
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from 'react'
import CodeBlock from '../../../components/layout/CodeBlock/CodeBlock'
import styles from '../integration-builder.module.css'
import { CheckoutSimulatorContext } from '../../../components/integration-builder/context/CheckoutSimulatorContext'
import { SnippetSchemaEnvironmentSampleFile } from '../../../components/integration-builder/code-selector/code-snippets/interfaces'
import withAnalytics from '../../../utils/withAnalytics'
import { CopyButton } from '../../../components/layout'
import { fileTypeOf } from '../../../utils'
import { codePreviewParser } from '../../../components/integration-builder/code-selector/code-snippets/helpers/codePreviewParser'
import templates from '../../../components/integration-builder/code-selector/code-snippets/checkout/templates'
import { codeSamplesUrl } from '../../../lib/assetsUrl'
import { File } from '../../../components/integration-builder/code-selector/code-snippets/interfaces/File'
import { CodeSelectorOption } from '../../../components/integration-builder/code-selector/interfaces'
import { removeGAParam } from '../../../components/pp-react-wrappers'

const Code: FunctionComponent = () => {
  type FileOption = {
    value: string
    primaryText: string
  }

  const fptiData = typeof window !== `undefined` && window.fpti
  const [isCopied, setIsCopied] = React.useState(false)

  const [files, setFiles] = useState<SnippetSchemaEnvironmentSampleFile[]>([])
  const [fileOptions, setFileOptions] = useState<FileOption[]>([])
  const [selectedFile, setSelectedFile] = useState('')

  const [selectedLanguage, setSelectedLanguage] = useState('html')
  const [selectedCodeSample, setSelectedCodeSample] = useState('')
  const {
    integrationType,
    snippets = {},
    parameters,
    options,
  } = useContext(CheckoutSimulatorContext)

  const [backendFiles, setBackendFiles] =
    useState<SnippetSchemaEnvironmentSampleFile[]>()
  const [frontendFiles, setFrontendFiles] =
    useState<SnippetSchemaEnvironmentSampleFile[]>()

  const getFallbackTemplate = (file: SnippetSchemaEnvironmentSampleFile) => {
    let templateFileCode = templates.find(
      (template) =>
        template.type === file?.type &&
        template.path === file?.path &&
        template.name === file?.name,
    )

    if (!templateFileCode) {
      setSelectedCodeSample('TEMPLATE NOT FOUND')
      return
    }

    setSelectedCodeSample(
      codePreviewParser(templateFileCode?.content!, { ...parameters, options }),
    )
  }

  /**
   * Function that builds the URL based on the integration,
   * option, and language buttons
   * @param baseUrl
   * @param paymentModel
   * @param file
   * @return url
   */
  const urlBuilder = (
    baseUrl: string,
    paymentModel: string,
    frontEndLaguage: string,
    buttons: string[],
    file: File,
  ): string => {
    let model = ''
    if (file.type === 'standard') {
      model =
        paymentModel === 'onetime-payment'
          ? 'one-time-payment/'
          : 'subscription-payment/'
      return `${baseUrl}/templates/${file.type}/${model}${file.fullPath}`
    }

    //If the type is advanced, check if 3DS option is selected or not. (Edge Case)
    if (file.name === 'checkout.ejs') {
      const path =
        buttons.includes('payment-buttons_3ds') && frontEndLaguage === 'html'
          ? `card-fields-with-3ds/server/html/views/${file.name}`
          : `card-fields/server/nodejs/html/views/${file.name}`
      return `${baseUrl}/templates/${file.type}/${path}`
    }

    model =
      buttons.includes('payment-buttons_3ds') && frontEndLaguage === 'html'
        ? 'card-fields-with-3ds/'
        : 'card-fields/'
    return `${baseUrl}/templates/${file.type}/${model}${file.fullPath}`
  }

  /**
   * This function is incharge of updating the code samples based on
   * the options and parameters selected.
   * @param fileName
   * @param option Takes the frontend language passed
   * @returns
   */
  const updateCodeSample = (fileName: string, option: CodeSelectorOption) => {
    const file = files.find((file) => file.name === fileName)
    if (!file) return
    const { value } = option
    const frontEndLanguageSelected: string | undefined = value as string
    const url = urlBuilder(
      codeSamplesUrl,
      parameters?.paymentModel,
      frontEndLanguageSelected,
      parameters?.buttons,
      file,
    )

    fetch(url, { method: 'GET', redirect: 'follow' })
      .then((res) => {
        if (res.status === 404) {
          getFallbackTemplate(file)
          throw new Error('CDN template not found')
        }
        return res.text()
      })
      .then((content) => {
        setSelectedCodeSample(
          codePreviewParser(content, { ...parameters, options }),
        )
      })
      .catch(() => {
        getFallbackTemplate(file)
      })
  }

  useEffect(() => {
    if (!snippets.backend || !snippets.frontend) return
    setBackendFiles(snippets.backend)
    setFrontendFiles(snippets.frontend)

    let file: string

    file =
      [...snippets.frontend, ...snippets.backend].find(
        (snippet) => snippet.name === selectedFile,
      )?.name || ''

    if (!file && snippets.frontend[0]) file = snippets.frontend[0].name
    if (!file && snippets.backend[0]) file = snippets.backend[0].name

    updateCodeSample(file, options[1])
    setSelectedFile(file)
    setSelectedLanguage(fileTypeOf(file))
  }, [snippets])

  useEffect(() => {
    if (!frontendFiles || !backendFiles) return
    setFiles([...frontendFiles!, ...backendFiles!])
  }, [backendFiles, frontendFiles, parameters])

  useEffect(() => {
    setFileOptions(
      files.map((file) => ({
        value: file.name,
        primaryText: file.name,
        file,
      })),
    )
    updateCodeSample(selectedFile, options[1])
  }, [files])

  const handleOnChangeFile: EventHandler<any> = (event: any) => {
    setSelectedFile(event.target.value)
    setSelectedLanguage(fileTypeOf(event.target.value))
    updateCodeSample(event.target.value, options[1])
  }

  const copySampleFile = async (selectedFile: string) => {
    setIsCopied(true)
    try {
      await navigator.clipboard.writeText(selectedCodeSample)
    } catch (err) {
      console.error('Failed to copy to clipboard')
    }
    if (fptiData && window.PAYPAL?.analytics?.instance) {
      const event = `code:${selectedFile}_${
        integrationType === 'standard' ? 'sc' : 'ac'
      }`
      window.PAYPAL.analytics.instance.record({
        data: {
          ...fptiData,
          component: 'devdiscoverynodeweb',
          space_key: 'SKEXJE',
          e: 'cl',
          link: event,
          event_action: event,
          page_name: fptiData && fptiData.page.replace(/:/g, '_'),
          event_name:
            fptiData && `${fptiData.page.replace(/:/g, '_')}_copy_sample_file`,
          pu: removeGAParam(window.location.href),
        },
      })
    }
    setTimeout(() => setIsCopied(false), 3000)
  }

  return (
    <div className={styles.checkout_simulator__code_container}>
      <div className={styles.checkout_simulator__file_selection_wrapper}>
        <DropdownMenu
          className={styles.checkout_simulator__file_selector}
          id="fileSelect"
          label="Code Source File"
          options={fileOptions}
          value={selectedFile}
          onChange={handleOnChangeFile}
        />
        <CopyButton onClick={() => copySampleFile(selectedFile)}>
          {isCopied ? (
            <Icon
              className={styles.integration_builder__copyIconSuccess}
              name="checkmark-background"
              color="sysColorSuccessMain"
            />
          ) : (
            <Icon
              className={styles.integration_builder__copyIcon}
              name="copy-to-clipboard"
              color="sysColorPrimaryMain"
            />
          )}
        </CopyButton>
      </div>
      <CodeBlock hasOwnCopyBlock={true} className={selectedLanguage}>
        {selectedCodeSample}
      </CodeBlock>
    </div>
  )
}

export default withAnalytics(Code)
